import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";

import {
  DEPLOY_SUSPENSE_WATCHDOG_MS,
  handleDeployStaleAssetFailure,
} from "../helpers/deployStaleAssetRecovery";
import { getIsCypressAut } from "../helpers/isCypressAut";

export const DEPLOY_WATCHDOG_STARTED_KEY_PREFIX =
  "adswebui_deploy_watchdog_started:";

export const getDeployWatchdogStartedKey = (testId) =>
  `${DEPLOY_WATCHDOG_STARTED_KEY_PREFIX}${testId || "default"}`;

const readWatchdogStartedAt = (storageKey) => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw == null) return null;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (_e) {
    return null;
  }
};

const writeWatchdogStartedAt = (storageKey, startedAt) => {
  try {
    sessionStorage.setItem(storageKey, String(startedAt));
  } catch (_e) {
    // ignore
  }
};

export const clearDeployWatchdogStartedAt = (testId) => {
  try {
    sessionStorage.removeItem(getDeployWatchdogStartedKey(testId));
  } catch (_e) {
    // ignore
  }
};

/**
 * Spinner with deploy-recovery watchdog (Suspense fallback, auth hang, etc.).
 *
 * By default the deadline resets when this unmounts (brief Suspense flashes
 * must not accumulate into a false Refresh UI). Pass
 * persistDeadlineAcrossRemounts for auth spinner across HashRouter flips.
 * Optional onWatchdogFire replaces the default soft-reload path.
 */
export const DeploySuspenseFallback = ({
  reportExhausted,
  navigate,
  timeoutMs = DEPLOY_SUSPENSE_WATCHDOG_MS,
  className,
  testId = "deploy-suspense-fallback",
  onWatchdogFire,
  persistDeadlineAcrossRemounts = false,
}) => {
  useEffect(() => {
    const storageKey = getDeployWatchdogStartedKey(testId);
    const now = Date.now();
    let startedAt = persistDeadlineAcrossRemounts
      ? readWatchdogStartedAt(storageKey)
      : null;
    if (startedAt == null) {
      startedAt = now;
      if (persistDeadlineAcrossRemounts) {
        writeWatchdogStartedAt(storageKey, startedAt);
      }
    }
    const remainingMs = Math.max(0, timeoutMs - (now - startedAt));

    const timer = setTimeout(() => {
      clearDeployWatchdogStartedAt(testId);
      if (typeof onWatchdogFire === "function") {
        onWatchdogFire();
        return;
      }
      handleDeployStaleAssetFailure({
        reportExhausted,
        ...(navigate ? { navigate } : {}),
      });
    }, remainingMs);

    return () => {
      clearTimeout(timer);
      // Successful resolve / leave spinner: drop deadline so later flashes
      // do not inherit a nearly-exhausted clock (ADSWEBUI-REACT-38B).
      if (!persistDeadlineAcrossRemounts) {
        clearDeployWatchdogStartedAt(testId);
      }
    };
  }, [
    reportExhausted,
    navigate,
    timeoutMs,
    testId,
    onWatchdogFire,
    persistDeadlineAcrossRemounts,
  ]);

  return (
    <div className={className} data-testid={testId}>
      <ProgressSpinner />
    </div>
  );
};

/** Cypress AUT: mount the watchdog outside the live app tree (controlled timeout). */
try {
  if (typeof window !== "undefined" && getIsCypressAut()) {
    window.__adsMountDeploySuspenseFallbackForTest = (
      container,
      props = {},
    ) => {
      const root = createRoot(container);
      root.render(<DeploySuspenseFallback {...props} />);
      return () => {
        root.unmount();
      };
    };
  }
} catch (_e) {
  // ignore
}
